<template>
  <div
    ref="$el"
    class="table-compare"
    :class="{ livestorm: isLivestorm }"
  >
    <div class="head">
      <div class="title">
        <img
          v-if="isLivestorm"
          alt="Livestorm logo"
          height="24"
          loading="lazy"
          src="@/assets/svg/logo-black.svg"
          width="142.75"
        >
        <UiComponentParagraph
          v-else
          :style="{ color: firstSoftware.companyColor.hex }"
        >
          {{ firstSoftware.companyName }}
        </UiComponentParagraph>
      </div>
      <div class="title">
        <UiComponentParagraph :style="{ color: secondSoftware.companyColor.hex }">
          {{
            secondSoftware.companyName
          }}
        </UiComponentParagraph>
      </div>
    </div>
    <div class="body">
      <div
        v-for="featureCategory in featuresCategories"
        :key="featureCategory.id"
        class="row"
        :class="{ active: activeCategories.includes(featureCategory.id) }"
      >
        <div
          class="head"
          @click="clickOnCategory(featureCategory.id)"
        >
          <UiComponentIcon type="chevron-right" />
          <UiComponentParagraph>{{ featureCategory.categoryName }}</UiComponentParagraph>
        </div>
        <div class="body">
          <div
            v-for="feature in features.filter(
              (feature) => feature.featureCategory.id === featureCategory.id,
            )"
            :key="feature.id"
            class="feature"
          >
            <div class="name">
              <UiComponentHeading
                as="paragraph"
                :level="5"
              >
                {{
                  feature.name
                }}
              </UiComponentHeading>
              <div
                v-if="feature.hoverDescription"
                class="info"
              >
                <UiComponentIcon type="info" />
                <div class="hover">
                  <UiComponentParagraph size="s">
                    {{
                      feature.hoverDescription
                    }}
                  </UiComponentParagraph>
                  <UiComponentParagraph
                    v-if="feature.showLastUpdate"
                    size="s"
                  >
                    {{
                      ratingDate
                    }}
                  </UiComponentParagraph>
                </div>
              </div>
            </div>
            <div
              class="value"
              :class="{ livestorm: isLivestorm }"
            >
              <UiComponentParagraph
                v-if="
                  firstSoftware[feature.key] !== '0'
                    && firstSoftware[feature.key] !== '1'
                "
              >
                {{ firstSoftware[feature.key] }}
              </UiComponentParagraph>
              <UiComponentIcon
                v-else
                :type="firstSoftware[feature.key] === '1' ? 'check' : 'x'"
              />
            </div>
            <div class="value">
              <UiComponentParagraph
                v-if="
                  secondSoftware[feature.key] !== '0'
                    && secondSoftware[feature.key] !== '1'
                "
              >
                {{ secondSoftware[feature.key] }}
              </UiComponentParagraph>
              <UiComponentIcon
                v-else
                :type="secondSoftware[feature.key] === '1' ? 'check' : 'x'"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
// Props
const props = defineProps({
  features: {
    required: true,
    type: Array,
  },
  featuresCategories: {
    required: true,
    type: Array,
  },
  firstSoftware: {
    required: true,
    type: Object,
  },
  isLivestorm: {
    default: false,
    type: Boolean,
  },
  ratingDate: {
    required: true,
    type: String,
  },
  secondSoftware: {
    required: true,
    type: Object,
  },
})

// Refs
const $el = ref(null)
const activeCategories = ref([])
const timeoutId = ref(null)

// Methods
function clickOnCategory(id) {
  if (activeCategories.value.includes(id))
    activeCategories.value = activeCategories.value.filter(
      cat => cat !== id,
    )
  else activeCategories.value.push(id)
}

function getActiveCategory(scrollY, offsetNavBar) {
  const categories = [...$el.value.querySelectorAll('.body .head > p')].map(
    (category) => {
      const { top } = category.getBoundingClientRect()
      return { text: category.textContent, top }
    },
  )
  const activeCategory = categories
    .reverse()
    .find(category => scrollY > category.top + scrollY - offsetNavBar)
  if (activeCategory) {
    return activeCategory.text
  }
  return categories[categories.length - 1].text
}

// Lifecycle Hooks
onBeforeUnmount(() => {
  if (timeoutId.value) {
    window.clearTimeout(timeoutId.value)
    timeoutId.value = null
  }
})

onMounted(() => {
  window.setTimeout(() => {
    window.clearTimeout(timeoutId.value)
    timeoutId.value = null
    activeCategories.value = props.featuresCategories
      .filter((_, index) => index === 0)
      .map(({ id }) => id)
  }, 100)
})

// Expose
defineExpose({
  $el,
  getActiveCategory,
})
</script>

<style lang="postcss" scoped>
.table-compare {
  width: 408px;
  @apply relative;

  @screen md {
    @apply w-full px-6;
  }

  @screen xl {
    @apply px-0;
    max-width: 1060px;
  }

  > .head {
    @apply flex justify-end;

    .title {
      height: 72px;
      width: 120px;
      @apply flex items-center justify-center border-2 border-grey-blue-200 border-b-0 rounded-t-[24px] bg-white self-end;

      &:first-child {
        @apply border-r-0 rounded-tr-none;
      }

      &:last-child {
        @apply rounded-tl-none;
      }

      @screen md {
        height: 94px;
        width: 212px;
      }

      @screen xl {
        width: 330px;
      }

      p {
        line-height: 54px;
        @apply w-full font-medium text-sm text-center;

        @screen md {
          line-height: 74px;
          padding: 0 10px;
          @apply px-4 text-lg;
        }
      }
    }
  }

  > .body {
    @apply w-fit border-2 border-grey-blue-200 bg-white;

    @screen md {
      @apply w-auto rounded-[24px] rounded-tr-none;
    }

    .row {
      @apply flex flex-col border-b-2 border-grey-blue-200;

      &.active {
        .head {
          i {
            @apply transform rotate-90;
          }
        }

        .body {
          @apply transition-all duration-500 ease-in-out;
          max-height: 2000px;
          will-change: max-height;

          .feature {
            @apply opacity-100 visible transition-all duration-500 ease-in-out;
          }
        }
      }

      &:last-child {
        @apply border-0;
      }

      .head {
        @apply px-3 py-8 flex items-center cursor-pointer;

        @screen md {
          @apply pl-10;
        }

        i {
          @apply mr-2;
        }

        p {
          @apply text-xl font-medium;
        }
      }

      .body {
        @apply flex flex-col max-h-0 transition-all duration-500 ease-in-out;
        will-change: max-height;

        .feature {
          @apply flex relative opacity-0 invisible border-b-2 border-grey-blue-200 transition-all duration-500 ease-in-out;

          &:first-child {
            .value {
              &:after {
                @apply top-0 h-full;

                @screen md {
                  top: -120%;
                  height: 220%;
                }
              }
            }
          }

          &:last-child {
            @apply border-none;
          }

          .name {
            min-width: 168px;
            @apply relative py-6 px-2 flex items-center w-full;

            @screen md {
              @apply pl-8;
            }

            .h5 {
              @apply text-xs;

              @screen md {
                @apply text-lg text-grey-blue-600;
              }
            }

            .info {
              @apply ml-2 flex justify-center items-center h-4 w-4 bg-grey-blue-400 rounded-full;

              @screen md {
                @apply relative;
              }

              i {
                @apply text-white leading-4 text-xl;
              }

              &:hover {
                .hover {
                  @apply flex flex-col;

                  &:after {
                    @apply absolute left-0 h-3 w-3 bg-grey-blue-100 transform rotate-45;
                    content: "";
                    bottom: -6px;

                    @screen md {
                      left: calc(50% - 6px);
                    }
                  }
                }
              }

              .hover {
                @apply absolute hidden left-0 w-screen z-50 px-8 py-6 bg-grey-blue-100 rounded shadow-xl leading-6;
                bottom: 80%;
                max-width: 330px;

                @screen md {
                  top: auto;
                  left: auto;
                  bottom: 30px;
                }
              }
            }
          }

          .value {
            width: 120px;
            @apply py-6 relative flex-shrink-0 text-center text-grey-blue-600;

            @screen md {
              width: 212px;
            }

            @screen xl {
              width: 329px;
            }

            &:after {
              content: "";
              @apply absolute h-full left-0 top-0 border-l-2 border-grey-blue-200;
            }

            &.livestorm {
              width: 122px;

              @screen md {
                width: 212px;
              }

              @screen xl {
                width: 369px !important;
              }
            }

            p {
              @apply text-sm;

              @screen md {
                @apply text-grey-blue-600 text-lg;
              }
            }

            i {
              @apply text-base;

              @screen md {
                @apply text-2xl;
              }
            }

            i.icon-check {
              @apply text-spring-green-600;
            }

            i.icon-x {
              @apply text-live-red-600;
            }
          }
        }
      }
    }
  }

  &.livestorm {
    .head {
      .title {
        &:first-child {
          height: 87px;
          width: 122px;
          @apply p-4 rounded-t-[24px];

          @screen md {
            height: 114px;
            width: 214px;
          }

          @screen xl {
            width: 370px;
          }
        }
      }
    }
  }
}
</style>
